<template>
  <div class="tfg-wrap">
    <header class="tfg-header">
      <div class="tfg-header-info">
        <div class="tfg-title-row">
          <h5 class="tfg-title">Por local</h5>
          <span
            class="tfg-mode-badge"
            :class="`tfg-mode-${windowMode.kind}`"
            :title="windowMode.tooltip"
          >
            <span v-if="windowMode.kind === 'live'" class="tfg-mode-dot"></span>
            <i v-else :class="windowMode.icon"></i>
            {{ windowMode.label }}
          </span>
        </div>
        <p class="tfg-subtitle">
          {{ tags.length }} {{ tags.length === 1 ? 'tag' : 'tags' }}
          <template v-if="windowLabel">{{ windowLabel }}</template>
          <template v-else>no período</template>
        </p>
      </div>
      <div class="tfg-actions">
        <div class="tfg-window-chips" title="Janela de tempo">
          <i class="bx bx-time-five tfg-chips-icon"></i>
          <button
            v-for="opt in windowOptions"
            :key="opt.value === null ? 'all' : opt.value"
            type="button"
            class="tfg-wchip"
            :class="{ 'tfg-wchip-active': windowMinutes === opt.value }"
            :title="opt.fullLabel || opt.label"
            @click="$emit('change-window', opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <div class="tfg-sort-chips" v-if="tags.length > 1" title="Ordenar por">
          <i class="bx bx-sort-alt-2 tfg-chips-icon"></i>
          <button
            v-for="opt in sortOptions"
            :key="opt.value"
            type="button"
            class="tfg-schip"
            :class="{ 'tfg-schip-active': sortBy === opt.value }"
            :title="opt.hint"
            @click="sortBy = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
    </header>

    <div v-if="!tags.length" class="tfg-empty">
      <i class="bx bx-map-alt tfg-empty-icon"></i>
      <p>Nenhuma tag teve movimento no período</p>
      <small>As tags aparecerão aqui assim que houver leituras</small>
    </div>

    <div v-else class="tfg-grid">
      <div
        v-for="tag in sortedTags"
        :key="tag.tag_id"
        class="tfg-card"
        :class="cardModifier(tag)"
        @click="onCardClick(tag)"
      >
        <div class="tfg-card-top">
          <div class="tfg-card-name">
            <span class="tfg-status-dot" :class="statusClass(tag)"></span>
            {{ tag.tag_name }}
          </div>
          <span v-if="tag.volume_share_pct > 0" class="tfg-share-badge">
            {{ tag.volume_share_pct }}%
          </span>
        </div>

        <div class="tfg-card-body">
          <div class="tfg-occupation">
            <span class="tfg-occ-value">
              <span v-if="tag.occupation_mode === 'fair_dwell'" class="tfg-occ-prefix">≈</span>{{ tag.occupation }}
            </span>
            <span class="tfg-occ-label">{{ tag.occupation_mode === 'fair_dwell' ? 'estimadas no estande' : 'dentro agora' }}</span>
          </div>

          <!-- Sparkline SVG embutida -->
          <div class="tfg-spark">
            <svg
              v-if="hasSparkline(tag)"
              viewBox="0 0 100 30"
              preserveAspectRatio="none"
              class="tfg-spark-svg"
            >
              <defs>
                <linearGradient :id="`tfg-grad-${tag.tag_id}`" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" :stop-color="sparkColor(tag)" stop-opacity="0.35"/>
                  <stop offset="100%" :stop-color="sparkColor(tag)" stop-opacity="0"/>
                </linearGradient>
              </defs>
              <path
                :d="sparkAreaPath(tag.sparkline)"
                :fill="`url(#tfg-grad-${tag.tag_id})`"
              />
              <path
                :d="sparkLinePath(tag.sparkline)"
                fill="none"
                :stroke="sparkColor(tag)"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <!-- Bolinha no último ponto -->
              <circle
                :cx="lastPointX(tag.sparkline)"
                :cy="lastPointY(tag.sparkline)"
                r="2"
                :fill="sparkColor(tag)"
              />
            </svg>
          </div>
        </div>

        <div class="tfg-card-foot">
          <!-- Modelo de estimativa (modo feira) -->
          <div v-if="tag.estimation" class="tfg-estim">
            <div class="tfg-estim-row">
              <span class="tfg-estim-pill" :title="`Modelo: ${tag.estimation.check_ins_per_min}/min × ${tag.estimation.dwell_minutes}min × ${tag.estimation.correction_factor}`">
                <i class="bx bx-time-five"></i>
                {{ tag.estimation.dwell_minutes }}min
              </span>
              <div v-if="tag.estimation.attractiveness > 0" class="tfg-score" :title="`Score de atratividade: ${tag.estimation.attractiveness}`">
                <i v-for="n in tag.estimation.attractiveness" :key="n" class="bx bxs-star tfg-score-star"></i>
              </div>
              <div v-if="hasFeatures(tag.estimation.features)" class="tfg-features">
                <span v-if="tag.estimation.features.has_demonstration" class="tfg-feat tfg-feat-demo" title="Demonstração">
                  <i class="bx bx-tv"></i>
                </span>
                <span v-if="tag.estimation.features.has_activation" class="tfg-feat tfg-feat-activation" title="Ativação">
                  <i class="bx bx-target-lock"></i>
                </span>
                <span v-if="tag.estimation.features.has_food" class="tfg-feat tfg-feat-food" title="Café/comida">
                  <i class="bx bx-coffee"></i>
                </span>
                <span v-if="tag.estimation.features.has_giveaway" class="tfg-feat tfg-feat-gift" title="Brinde">
                  <i class="bx bx-gift"></i>
                </span>
                <span v-if="tag.estimation.features.queue_minutes > 0" class="tfg-feat tfg-feat-queue" :title="`Fila ~${tag.estimation.features.queue_minutes}min`">
                  <i class="bx bx-loader-circle"></i>
                </span>
              </div>
            </div>
          </div>

          <!-- Barra de % comparecimento -->
          <div v-if="tag.attendance_pct !== null" class="tfg-att">
            <div class="tfg-att-bar">
              <div
                class="tfg-att-fill"
                :style="{ width: Math.min(100, tag.attendance_pct) + '%' }"
              ></div>
            </div>
            <div class="tfg-att-row">
              <span class="tfg-att-pct">{{ tag.attendance_pct }}%</span>
              <span class="tfg-att-detail">
                {{ tag.attended_unique }} de {{ tag.eligible_total }} elegíveis
              </span>
            </div>
          </div>

          <div class="tfg-stats">
            <span class="tfg-stat" :title="'Entradas no período'">
              <i class="bx bx-log-in-circle"></i> {{ tag.ins }}
            </span>
            <span v-if="showCheckout" class="tfg-stat" :title="'Saídas no período'">
              <i class="bx bx-log-out-circle"></i> {{ tag.outs }}
            </span>
            <span class="tfg-stat tfg-stat-time" :title="'Última leitura'">
              <i class="bx bx-time"></i> {{ formatLastEvent(tag) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const SPARK_W = 100
const SPARK_H = 30

export default {
  name: 'tag-flow-grid',
  props: {
    tags: { type: Array, default: () => [] },
    showCheckout: { type: Boolean, default: true },
    windowMinutes: { type: Number, default: null }
  },
  data: () => ({
    sortBy: 'volume',
    sortOptions: [
      { value: 'volume', label: 'Volume', hint: 'Mais entradas no período (total bruto de leituras de entrada).' },
      { value: 'occupation', label: 'Ocupação', hint: 'Quantos estão dentro/circulando agora (entradas − saídas, ou estimativa em modo feira).' },
      { value: 'attendance', label: 'Compareceu', hint: '% de elegíveis que de fato apareceram (atendimento / convidados ou inscritos).' },
      { value: 'recent', label: 'Recente', hint: 'Última leitura mais próxima de agora — quem está com fluxo mais quente.' }
    ],
    windowOptions: [
      { value: null, label: 'Tudo', fullLabel: 'Período inteiro do filtro do header' },
      { value: 5, label: '5m', fullLabel: '5 minutos' },
      { value: 15, label: '15m', fullLabel: '15 minutos' },
      { value: 30, label: '30m', fullLabel: '30 minutos' },
      { value: 60, label: '1h', fullLabel: '1 hora' },
      { value: 240, label: '4h', fullLabel: '4 horas' },
      { value: 480, label: '8h', fullLabel: '8 horas' },
      { value: 1440, label: '1d', fullLabel: '1 dia' }
    ]
  }),
  computed: {
    sortedTags () {
      const arr = [...this.tags]
      switch (this.sortBy) {
        case 'occupation':
          arr.sort((a, b) => (b.occupation || 0) - (a.occupation || 0))
          break
        case 'attendance':
          arr.sort((a, b) => (b.attendance_pct || 0) - (a.attendance_pct || 0))
          break
        case 'recent':
          arr.sort((a, b) => (a.minutes_since_last || 0) - (b.minutes_since_last || 0))
          break
        case 'volume':
        default:
          arr.sort((a, b) => (b.ins || 0) - (a.ins || 0))
      }
      return arr
    },
    /**
     * Maior ocupação atual do conjunto — base pra calcular intensidade
     * relativa de cada card (heat scale verde/laranja/vermelho).
     */
    maxOccupation () {
      if (!this.tags.length) return 0
      return this.tags.reduce((m, t) => Math.max(m, t.occupation || 0), 0)
    },
    windowLabel () {
      if (!this.windowMinutes) return ''
      const opt = this.windowOptions.find(o => o.value === this.windowMinutes)
      if (!opt) return ''
      return `nos últimos ${(opt.fullLabel || opt.label).toLowerCase()}`
    },
    /**
     * Indicador contextual no header — substitui o antigo "X silenciosas".
     * O label muda conforme a janela escolhida pra deixar claro o que o
     * usuário está olhando: leitura "ao vivo" (curto prazo) vs panorama
     * acumulado (longo prazo).
     *
     * Faixas:
     *  - ≤ 30 min  → 'live'      "Tempo real" (com pulsing dot verde)
     *  - 1 h       → 'recent'    "Última hora"
     *  - 4 h, 8 h  → 'trend'     "Tendência" (X horas)
     *  - 1 d       → 'daily'     "Diário"
     *  - Período   → 'period'    "Acumulado" (usa filtro do header)
     */
    windowMode () {
      const w = this.windowMinutes
      if (w === null || w === undefined) {
        return {
          kind: 'period',
          label: 'Acumulado',
          icon: 'bx bx-calendar-event',
          tooltip: 'Visão acumulada do período selecionado no filtro do dashboard'
        }
      }
      if (w <= 30) {
        return {
          kind: 'live',
          label: 'Tempo real',
          icon: 'bx bx-broadcast',
          tooltip: 'Janela curta — o que está acontecendo agora'
        }
      }
      if (w <= 60) {
        return {
          kind: 'recent',
          label: 'Última hora',
          icon: 'bx bx-time',
          tooltip: 'Movimento da última hora — boa pra ver tendência sem ruído'
        }
      }
      if (w <= 480) {
        const hours = Math.round(w / 60)
        return {
          kind: 'trend',
          label: `Tendência ${hours}h`,
          icon: 'bx bx-trending-up',
          tooltip: `Panorama das últimas ${hours} horas — bom pra entender o turno`
        }
      }
      return {
        kind: 'daily',
        label: 'Diário',
        icon: 'bx bx-calendar',
        tooltip: 'Visão acumulada das últimas 24 horas'
      }
    }
  },
  methods: {
    statusClass (tag) {
      if (tag.is_stale) return 'tfg-status-stale'
      if (tag.minutes_since_last > 5) return 'tfg-status-slow'
      return 'tfg-status-live'
    },
    /**
     * Heat scale relativa: classifica cada card como fraco/médio/forte
     * conforme a ocupação atual da tag em relação ao MÁXIMO do conjunto.
     *
     * Algoritmo:
     *   1. occupation = 0  → 'empty' (cinza)
     *   2. maxOccupation < 4 → 'weak' uniforme (volume insuficiente p/ rankear)
     *   3. score = occupation / maxOccupation
     *      - score >= 0.66 → 'strong' (verde) — top tier do evento
     *      - score >= 0.33 → 'medium' (laranja) — meio
     *      - resto         → 'weak' (vermelho) — baixo
     *
     * Por que relativo: 10 pessoas é "forte" num evento de 50 e "fraco" num
     * evento de 1000. Ancorar no max do conjunto adapta automaticamente.
     *
     * Stale tem precedência: tag silenciosa pinta com `tfg-card-stale`
     * independente da heat score.
     */
    heatClass (tag) {
      const occ = tag.occupation || 0
      if (occ === 0) return 'tfg-heat-empty'
      const max = this.maxOccupation
      if (max < 4) return 'tfg-heat-weak'
      const score = occ / max
      if (score >= 0.66) return 'tfg-heat-strong'
      if (score >= 0.33) return 'tfg-heat-medium'
      return 'tfg-heat-weak'
    },
    cardModifier (tag) {
      return {
        'tfg-card-stale': tag.is_stale,
        [this.heatClass(tag)]: !tag.is_stale
      }
    },
    sparkColor (tag) {
      if (tag.is_stale) return '#f46a6a'
      const heat = this.heatClass(tag)
      if (heat === 'tfg-heat-strong') return '#34c38f'
      if (heat === 'tfg-heat-medium') return '#f1b44c'
      if (heat === 'tfg-heat-weak') return '#f46a6a'
      return '#8b90a5'
    },
    hasSparkline (tag) {
      return Array.isArray(tag.sparkline) && tag.sparkline.length > 0
    },
    sparkPoints (data) {
      if (!data || data.length === 0) return []
      const max = Math.max(...data, 1)
      const stepX = SPARK_W / Math.max(1, data.length - 1)
      return data.map((v, i) => ({
        x: i * stepX,
        y: SPARK_H - (v / max) * (SPARK_H - 4) - 2
      }))
    },
    sparkLinePath (data) {
      const pts = this.sparkPoints(data)
      if (pts.length === 0) return ''
      // smooth curve usando comando S (smooth quadratic)
      let path = `M${pts[0].x},${pts[0].y}`
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1]
        const curr = pts[i]
        const cx = (prev.x + curr.x) / 2
        path += ` Q${prev.x},${prev.y} ${cx},${(prev.y + curr.y) / 2}`
        path += ` T${curr.x},${curr.y}`
      }
      return path
    },
    sparkAreaPath (data) {
      const pts = this.sparkPoints(data)
      if (pts.length === 0) return ''
      const linePath = this.sparkLinePath(data)
      const last = pts[pts.length - 1]
      const first = pts[0]
      return `${linePath} L${last.x},${SPARK_H} L${first.x},${SPARK_H} Z`
    },
    lastPointX (data) {
      const pts = this.sparkPoints(data)
      return pts.length ? pts[pts.length - 1].x : 0
    },
    lastPointY (data) {
      const pts = this.sparkPoints(data)
      return pts.length ? pts[pts.length - 1].y : 0
    },
    formatLastEvent (tag) {
      const m = tag.minutes_since_last
      if (m === null || m === undefined) return '—'
      if (m < 1) return 'agora'
      if (m < 60) return `${m}min`
      const h = Math.floor(m / 60)
      return `${h}h`
    },
    hasFeatures (f) {
      if (!f) return false
      return f.has_demonstration || f.has_activation || f.has_food || f.has_giveaway || (f.queue_minutes > 0)
    },
    onCardClick (tag) {
      this.$emit('select-tag', tag)
    }
  }
}
</script>

<style lang="scss" scoped>
.tfg-wrap {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
}

.tfg-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.tfg-header-info {
  min-width: 0;
}

.tfg-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.tfg-title {
  font-size: 14px;
  font-weight: 700;
  color: #1a1d26;
  margin: 0;
}

.tfg-subtitle {
  font-size: 12px;
  color: #8b90a5;
  margin: 0;
}

/* Indicador contextual da janela — substitui a antiga "X silenciosas".
   Cor e estilo mudam conforme o modo: live (pulsa verde), recent/trend
   (azul neutro), daily/period (cinza). */
.tfg-mode-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;

  i { font-size: 12px; }

  &.tfg-mode-live {
    background: rgba(52, 195, 143, 0.12);
    color: #2a9d6f;
  }

  &.tfg-mode-recent {
    background: rgba(85, 110, 230, 0.1);
    color: #556ee6;
  }

  &.tfg-mode-trend {
    background: rgba(85, 110, 230, 0.08);
    color: #4a5068;
  }

  &.tfg-mode-daily {
    background: #f0f1f6;
    color: #4a5068;
  }

  &.tfg-mode-period {
    background: #f0f1f6;
    color: #8b90a5;
  }
}

.tfg-mode-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #34c38f;
  box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.7);
  animation: tfg-mode-pulse 1.6s ease-out infinite;
}

@keyframes tfg-mode-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.7); }
  70%  { box-shadow: 0 0 0 7px rgba(52, 195, 143, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0); }
}

.tfg-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap; /* janela e ordenação podem stackar verticalmente,
                     mas cada grupo individual NÃO quebra (nowrap nos filhos) */
}

.tfg-window-chips,
.tfg-sort-chips {
  display: inline-flex;
  align-items: center;
  background: #f5f6fa;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 2px 4px 2px 6px;
  gap: 1px;
  flex-wrap: nowrap; /* chips internos nunca quebram em duas linhas */
  max-width: 100%;
  overflow-x: auto; /* fallback se um nome muito longo aparecer */

  /* Scrollbar fina e discreta — só aparece se realmente overflow */
  &::-webkit-scrollbar { height: 0; }
  scrollbar-width: none;
}

.tfg-chips-icon {
  font-size: 14px;
  color: #8b90a5;
  margin-right: 4px;
  flex-shrink: 0;
}

.tfg-wchip,
.tfg-schip {
  background: transparent;
  border: 0;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #4a5068;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.12s ease;
  white-space: nowrap;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;

  &:hover { background: #fff; }
}

.tfg-wchip {
  min-width: 28px; /* uniforme: "5m" e "15m" mesma largura visual */
  text-align: center;
}

.tfg-wchip-active,
.tfg-schip-active {
  background: #fff;
  color: #556ee6;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.08);
}

.tfg-empty {
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8b90a5;
  text-align: center;

  .tfg-empty-icon {
    font-size: 42px;
    color: #d0d5dd;
    margin-bottom: 8px;
  }
  p { font-size: 13px; font-weight: 600; margin: 0 0 4px 0; color: #4a5068; }
  small { font-size: 12px; }
}

.tfg-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.tfg-card {
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #d4d8e3;
    box-shadow: 0 6px 20px rgba(26, 29, 38, 0.08);
    transform: translateY(-2px);
  }

  &.tfg-card-stale {
    background: linear-gradient(135deg, #fff 0%, #fff5f5 100%);
    border-color: rgba(244, 106, 106, 0.25);
  }

  /* Heat scale: borda lateral colorida + tint discreto no fundo */
  &.tfg-heat-strong {
    border-left: 3px solid #34c38f;
    background: linear-gradient(135deg, #fff 0%, #f5fbf8 100%);

    .tfg-occ-value { color: #2a9d6f; }
  }
  &.tfg-heat-medium {
    border-left: 3px solid #f1b44c;
    background: linear-gradient(135deg, #fff 0%, #fffaf0 100%);

    .tfg-occ-value { color: #b8801f; }
  }
  &.tfg-heat-weak {
    border-left: 3px solid #f46a6a;
    background: linear-gradient(135deg, #fff 0%, #fff7f7 100%);

    .tfg-occ-value { color: #d04848; }
  }
  &.tfg-heat-empty {
    border-left: 3px solid #d0d5dd;

    .tfg-occ-value { color: #8b90a5; }
  }
}

.tfg-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.tfg-card-name {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #1a1d26;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tfg-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;

  &.tfg-status-live  { background: #34c38f; box-shadow: 0 0 0 2px rgba(52, 195, 143, 0.18); }
  &.tfg-status-slow  { background: #f1b44c; }
  &.tfg-status-stale { background: #f46a6a; }
}

.tfg-share-badge {
  font-size: 10px;
  font-weight: 700;
  color: #556ee6;
  background: rgba(85, 110, 230, 0.1);
  padding: 2px 6px;
  border-radius: 6px;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}

.tfg-card-body {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.tfg-occupation {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.tfg-occ-value {
  font-size: 28px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.tfg-occ-prefix {
  font-size: 18px;
  font-weight: 500;
  color: #8b90a5;
  margin-right: 2px;
  vertical-align: 0.18em;
}

.tfg-occ-label {
  font-size: 10px;
  color: #8b90a5;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
  margin-top: 4px;
}

.tfg-spark {
  flex: 1;
  height: 30px;
  max-width: 110px;
}

.tfg-spark-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.tfg-card-foot {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid #f0f1f6;
}

.tfg-estim {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tfg-estim-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.tfg-estim-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  background: rgba(85, 110, 230, 0.08);
  color: #556ee6;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;

  i { font-size: 12px; }
}

.tfg-score {
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.tfg-score-star {
  font-size: 10px;
  color: #f1b44c;
}

.tfg-features {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
}

.tfg-feat {
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  cursor: help;
  transition: transform 0.15s ease, background 0.15s ease;

  i { font-size: 12px; }

  &:hover { transform: scale(1.15); }

  &.tfg-feat-demo       { background: rgba(85, 110, 230, 0.12); color: #556ee6; }
  &.tfg-feat-activation { background: rgba(139, 92, 246, 0.12); color: #8b5cf6; }
  &.tfg-feat-food       { background: rgba(241, 180, 76, 0.16); color: #b8801f; }
  &.tfg-feat-gift       { background: rgba(236, 72, 153, 0.12); color: #ec4899; }
  &.tfg-feat-queue      { background: rgba(244, 106, 106, 0.12); color: #d04848; }
}

.tfg-att {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tfg-att-bar {
  height: 4px;
  background: #f0f1f6;
  border-radius: 99px;
  overflow: hidden;
}

.tfg-att-fill {
  height: 100%;
  background: linear-gradient(90deg, #556ee6 0%, #34c38f 100%);
  border-radius: 99px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.tfg-att-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.tfg-att-pct {
  font-size: 12px;
  font-weight: 700;
  color: #34c38f;
}

.tfg-att-detail {
  font-size: 10px;
  color: #8b90a5;
  font-weight: 500;
}

.tfg-stats {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.tfg-stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #4a5068;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  i { font-size: 13px; opacity: 0.7; }

  &.tfg-stat-time {
    margin-left: auto;
    color: #8b90a5;
    font-weight: 500;
  }
}
</style>
